/**
 * Resize an uploaded File to at most MAX_DIM px on the longest edge while
 * preserving aspect ratio, then encode as JPEG. Returns a `data:` URI ready
 * to ship to the POST /api/operations/:id/report-photos endpoint.
 *
 * Why client-side: keeping the resize in the browser saves Worker CPU and
 * upload bandwidth — a 4 MB phone photo becomes a ~150–400 KB JPEG before
 * leaving the device.
 *
 * Images already within bounds are still re-encoded as JPEG so the storage
 * format stays consistent (HEIC, PNG screenshots, etc. all become JPEGs).
 *
 * PNG mode (`{ format: 'png' }`) is the exception, added for the sticker library:
 * a sticker needs its alpha channel, and the white fill below — which exists
 * because JPEG has no alpha and would otherwise render transparency black —
 * would paint a white box around every one of them.
 */
const MAX_DIM = 1080
const JPEG_QUALITY = 0.85

/**
 * @param {File} file
 * @param {{maxDim?: number, format?: 'jpeg'|'png'}} [opts]
 */
export async function resizeImageFile(file, opts = {}) {
  if (!file) throw new Error('no file')
  const maxDim = opts.maxDim || MAX_DIM
  const png    = opts.format === 'png'
  const bitmap = await loadBitmap(file)
  const { width: srcW, height: srcH } = bitmap

  let w = srcW, h = srcH
  if (Math.max(srcW, srcH) > maxDim) {
    if (srcW >= srcH) {
      w = maxDim
      h = Math.round((srcH / srcW) * maxDim)
    } else {
      h = maxDim
      w = Math.round((srcW / srcH) * maxDim)
    }
  }

  const canvas = document.createElement('canvas')
  canvas.width  = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  // White background — JPEG has no alpha; without this transparent PNGs come
  // out with black backgrounds. Skipped for PNG, where keeping the alpha is the
  // entire reason the caller asked for PNG.
  if (!png) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
  }
  ctx.drawImage(bitmap, 0, 0, w, h)
  if (bitmap.close) bitmap.close()

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => b ? resolve(b) : reject(new Error('canvas encode failed')),
      png ? 'image/png' : 'image/jpeg',
      png ? undefined : JPEG_QUALITY,
    )
  })
  const dataUri = await blobToDataUri(blob)

  return {
    dataUri,
    width: w,
    height: h,
    bytes: blob.size,
    filename: replaceExtension(file.name || 'photo', png ? 'png' : 'jpg'),
    resized: w !== srcW || h !== srcH,
  }
}

async function loadBitmap(file) {
  // createImageBitmap respects EXIF orientation when imageOrientation: 'from-image'
  // is supported. Falls back to <img> on browsers where the option isn't honored.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch { /* fall through */ }
  }
  return await loadViaImg(file)
}

function loadViaImg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('image decode failed'))
    }
    img.src = url
  })
}

function blobToDataUri(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload  = () => resolve(String(r.result || ''))
    r.onerror = () => reject(new Error('read failed'))
    r.readAsDataURL(blob)
  })
}

function replaceExtension(name, ext) {
  const base = String(name).replace(/\.[^.]+$/, '')
  return `${base}.${ext}`
}
